import { Component, computed, input, model, ChangeDetectionStrategy } from '@angular/core';
import { DokaCheckbox } from '@doka/angular-core';
import { I18NextEagerPipe } from 'angular-i18next';

export interface CountryCheckboxOption {
  id: string;
  label: string;
  description?: string | null;
  /** Shown checked and not selectable, e.g. for countries that are already planned. */
  locked?: boolean;
  /** Not selectable and shown unchecked, e.g. for countries without recipients. */
  disabled?: boolean;
}

/**
 * Country checkbox list with a "select all" toggle. Locked and disabled options are never part of the selection.
 */
@Component({
  selector: 'app-country-checkbox-list',
  templateUrl: './country-checkbox-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DokaCheckbox, I18NextEagerPipe],
})
export class CountryCheckboxListComponent {
  public readonly options = input.required<CountryCheckboxOption[]>();
  public readonly selectedIds = model.required<ReadonlySet<string>>();
  /** Prefix of the data-testids: `{prefix}-select-all`, `{prefix}-selected-count` and `{prefix}-country-{countryId}`. */
  public readonly testIdPrefix = input.required<string>();

  protected readonly selectableIds = computed(() =>
    this.options()
      .filter((option) => !option.locked && !option.disabled)
      .map((option) => option.id),
  );

  protected readonly allSelected = computed(() => {
    const selectableIds = this.selectableIds();
    return selectableIds.length > 0 && selectableIds.every((id) => this.selectedIds().has(id));
  });

  protected readonly someSelected = computed(
    () => !this.allSelected() && this.selectableIds().some((id) => this.selectedIds().has(id)),
  );

  protected toggleAll(selected: boolean) {
    this.selectedIds.set(new Set(selected ? this.selectableIds() : []));
  }

  protected toggle(option: CountryCheckboxOption, selected: boolean) {
    if (option.locked || option.disabled) {
      return;
    }

    const selection = new Set(this.selectedIds());
    if (selected) {
      selection.add(option.id);
    } else {
      selection.delete(option.id);
    }
    this.selectedIds.set(selection);
  }
}
